import { EXPANDED_DREAMS } from './additionalDreams';
import { DreamTopic, DreamCategory } from '../types';
import { enrichDreamArticle } from '../lib/dreamArticleEnricher';
import { DictionaryEntry, DICTIONARY_DB, findDictionaryBySlug, EDITORIAL_RESOLUTION } from './dictionaryDb';
import { EDITORIAL_ENRICHMENT } from './editorialEnrichment';
export type { DreamTopic, DreamCategory };

export const ARABIC_LETTERS: string[] = [
  'أ', 'ب', 'ت', 'ث', 'ج', 'ح', 'خ', 'د', 'ذ', 'ر', 'ز', 'س', 'ش', 'ص', 'ض', 'ط', 'ظ', 'ع', 'غ', 'ف', 'ق', 'ك', 'ل', 'م', 'ن', 'هـ', 'و', 'ي'
];

export const DREAM_CATEGORIES: DreamCategory[] = [
  { slug: 'animals-reptiles', name: 'حيوانات وزواحف', description: 'تفسير رؤية الحيوانات والطيور والزواحف والحشرات في المنام', icon: '🐍' },
  { slug: 'gold-money', name: 'أموال وذهب ومعادن', description: 'تفسير رؤية الفلوس، الذهب، الفضة، والكنوز في المنام', icon: '💰' },
  { slug: 'events-death-marriage', name: 'أحداث ومناسبات', description: 'تفسير رؤيا الموت، الزواج، السفر، الولادة، والاحتفالات', icon: '💍' },
  { slug: 'nature-elements', name: 'طبيعة وعناصر', description: 'تفسير رؤية البحر، المطر، النار، السماء، والرياح في المنام', icon: '🌊' },
  { slug: 'body-health', name: 'أعضاء الجسم والصحة', description: 'تفسير تساقط الأسنان، الشعر، العيون، والمرض في المنام', icon: '🦷' },
  { slug: 'buildings-places', name: 'أماكن وبيوت ومباني', description: 'تفسير رؤية البيت الجديد، المساجد، القبور، والقصور', icon: '🏛️' },
  { slug: 'people-figures', name: 'أشخاص ووظائف', description: 'تفسير رؤية الأنبياء والملوك والقضاة والوظائف في المنام', icon: '👤' },
  { slug: 'clothing-adornment', name: 'ملابس وزينة', description: 'تفسير رؤية الثياب والعمائم والخواتم والزينة في المنام', icon: '👕' },
  { slug: 'food-drink', name: 'طعام وشراب', description: 'تفسير رؤية الطعام والشراب والفواكه في المنام', icon: '🍞' },
  { slug: 'misc', name: 'أشياء ومتفرقات', description: 'تفسير رؤية الأدوات والأشياء والمفردات المتنوعة في المنام', icon: '✨' },
];

const INITIAL_DREAMS: DreamTopic[] = [
  {
    slug: 'snake',
    arabicSlug: 'تفسير-حلم-الثعبان',
    name: 'الثعبان والحية',
    letter: 'ث',
    category: 'حيوانات وزواحف',
    categorySlug: 'animals-reptiles',
    title: 'تفسير حلم الثعبان في المنام لابن سيرين والنابلسي بالتفصيل الشامل',
    metaDescription: 'تعرف على تفسير حلم الثعبان في المنام بالتفصيل لابن سيرين والنابلسي للعزباء والمتزوجة والحامل والرجل. دلالات الثعبان الأسود والأصفر والأخضر ولدغة الثعبان.',
    keywords: ['تفسير حلم الثعبان', 'رؤية الثعبان في المنام', 'الثعبان الاسود في المنام', 'لدغة الثعبان', 'تفسير ابن سيرين للثعبان', 'قتل الثعبان في الحلم'],
    summary: 'يدل الثعبان في المنام عند جمهور المفسرين كابن سيرين والنابلسي على العداوة، فكلما كبر حجم الثعبان وعظم سمه كان العدو أشد بأساً وبطشاً، وقد يدل أحياناً على عداوة الأقارب أو الحاقدين والحاسدين.',
    ibnSirinInterpretation: 'يرى الإمام ابن سيرين أن الحية أو الثعبان في الرؤيا عدو للإنسان، وذلك لأن إبليس اللعين توصل بها إلى إخراج آدم عليه السلام من الجنة. وعداوة كل ثعبان على قدر عظمه ونكايته وسمه. فمن رأى أنه يقاتل ثعباناً أو ينازعه فإنه يقاتل عدواً، فإن قتله ظفر بعدوه، وإن لدغه الثعبان ناله مكروه من عدوه بقدر اللدغة. كما أن رؤية الثعابين تدخل البيت وتخرج منه تدل على أعداء من أهل البيت أو الأقارب المقربين.',
    nabulsiInterpretation: 'أفاد الشيخ النابلسي بأن الثعبان قد يدل على سلطان أو دولة أو صاحب مال وجاه إن كان مطواعاً للرائي لا يؤذيه، ولكن إذا ظهر بصورة مخيفة أو هاجم الرائي فهو عدو حاسد ذو مكر وخديعة. والثعبان المائي يدل على عون للظالم أو رزق فيه شبهة، بينما خروج الثعابين من البطن يدل على شرور ومكائد من الأهل والأولاد.',
    cases: {
      single: 'إذا رأت العزباء الثعبان في منامها فذلك تحذير من شخص ماكر يتقرب منها تحت ستار الحب أو الصداقة، وقتله في المنام بشارة بالنجاح والتخلص من العلاقات السامة.',
      married: 'رؤية الثعبان للمتزوجة تشير إلى وجود امرأة حسودة تحاول إيقاع الخلافات بينها وبين زوجها، ولدغته تحذير من ضائقة أو مكيدة تدبر لها.',
      pregnant: 'يدل الثعبان للحامل على المخاوف والمتاعب النفسية المرتبطة بالولادة، ورؤية الثعبان الصغير بشارة بمولود ذكر ولكن قد تصاحبه بعض المشقة في تربيته.',
      divorced: 'ترمز رؤية الثعبان للمطلقة إلى الصعوبات والأقاويل الباطلة التي تحيط بها من أشخاص مقربين، والتخلص منه يرمز لبداية مرحلة جديدة مليئة بالطمأنينة واسترداد الحقوق.',
      man: 'رؤية الثعبان للرجل في مكان العمل تدل على منافس غير شريف يكيد له، وإن قتله في بيته دل على الانتصار على الخصوم وحماية أسرته من الحسد.'
    },
    keyVariations: [
      { title: 'الثعبان الأسود', description: 'أشد أنواع الأعداء عداوة وبغضاً، وغالباً ما يرمز إلى سحر أو حسد شديد أو خصومة مع شخص صاحب نفوذ.' },
      { title: 'الثعبان الأخضر', description: 'يدل على شخص يظهر التقوى والصلاح ولكنه يضمر الخبث، أو يدل على تأجيل لبعض الرزق والفرص.' },
      { title: 'الثعبان الأصفر', description: 'يرمز بشكل مباشر إلى المرض أو الوعكة الصحية، والحسد الشديد من شخص حاقد يتمنى زوال النعمة.' },
      { title: 'لدغة الثعبان في اليد أو القدم', description: 'إذا كانت اللدغة في اليد اليمنى فتشير إلى مال ورزق بعد تعب، وفي اليد اليسرى تنبيه للابتعاد عن الخطايا والذنوب.' }
    ],
    faqs: [
      { question: 'هل رؤية الثعبان في المنام تدل دائماً على شر؟', answer: 'ليس دائماً؛ فإذا كان الثعبان مطيعاً للرائي دون خوف أو أذى، أو كان يتكلم بكلام حسن، فقد يدل على نيل هيبة أو مال أو منصب رفيع.' },
      { question: 'ما معنى الهروب من الثعبان في الحلم؟', answer: 'الهروب من الثعبان مع الخوف منه يرمز إلى النجاة من مكر الأعداء والأمن من المخاوف والأخطار.' },
      { question: 'ما دلالة قتل الثعبان في المنام؟', answer: 'قتل الثعبان من الرؤى المحمودة باتفاق المعبرين، وهو علامة صريحة على النصر على الأعداء وزوال الهم والكرب والشفاء من المرض.' }
    ],
    relatedSlugs: ['gold', 'teeth-falling', 'death', 'sea', 'black-cat'],
    datePublished: '2025-01-10',
    dateModified: '2026-08-30'
  },
  {
    slug: 'gold',
    arabicSlug: 'تفسير-حلم-الذهب',
    name: 'الذهب والمجوهرات',
    letter: 'ذ',
    category: 'أموال وذهب ومعادن',
    categorySlug: 'gold-money',
    title: 'تفسير حلم الذهب في المنام بالتفصيل لابن سيرين والنابلسي للنساء والرجال',
    metaDescription: 'دليل شامل لتفسير حلم الذهب في المنام لابن سيرين والنابلسي. لبس الذهب، العثور على سبيكة ذهب، إهداء الذهب للعزباء والمتزوجة والحامل والمطلقة والرجل.',
    keywords: ['تفسير حلم الذهب', 'رؤية الذهب في المنام', 'لبس الذهب للمتزوجة', 'خاتم ذهب للعزباء', 'تفسير ابن سيرين للذهب', 'سبيكة الذهب في المنام'],
    summary: 'يختلف تفسير الذهب باختلاف جنس الرائي وسياق الرؤيا؛ فالذهب للنساء زينة ورزق وفرح وبشارة، بينما كرهه كثير من المعبرين للرجال بسبب لفظه المشتق من الذهاب ولونه الأصفر الشبيه بالحزن.',
    ibnSirinInterpretation: 'ذكر ابن سيرين أن الذهب لا يُحمد في التأويل لكثرة اشتقاقه من الذهاب وصفرة لونه الدالة على الهم، باستثناء إذا لبسته المرأة أو كان مصوغاً كالحلي والخواتم والقلائد. فمن رأى أنه أصاب ذهباً مجهولاً أو سبيكة ذهب ناله هم وغم مساوٍ لقيمتها أو غضب عليه صاحب سلطان.',
    nabulsiInterpretation: 'يرى النابلسي أن الذهب المصوغ محمود في الرؤى ويدل على الأفراح والمناسبات السعيدة والأولاد، بينما السبائك والذهب الخام تدل على الخسائر المالية والهموم العارضة. ورؤية تحول الفضة إلى ذهب تدل على تحسن الحال وزيادة الشرف والمكانة.',
    cases: {
      single: 'بشارة باقتراب الخطوبة أو الزواج من شخص ميسور الحال، ولبس الخاتم أو الإسوارة يدل على رفعة شأن ونجاح في العمل أو الدراسة.',
      married: 'يدل على سعة الرزق والاستقرار العاطفي والأسري، ولبس القلادة الذهبية دلالة على حمل قريب أو تولي مسؤولية مباركة.',
      pregnant: 'الذهب للحامل إشارة إلى جنس الجنين؛ فالخاتم والقرط يرمزان للمولود الذكر، بينما السلاسل والأساور ترمز للمولودة الأنثى والله أعلم.',
      divorced: 'شراء الذهب أو إهداؤه للمطلقة علامة على تيسير الأمور وتخطي الصعاب والتعويض الجميل برزق مبارك أو زواج جديد.',
      man: 'لبس الذهب للرجل غير محمود ويدل على هموم أو مخالفة للسنة، لكن أخذ الذهب دون لبسه قد يدل على الميراث أو إدارة تجارة.'
    },
    keyVariations: [
      { title: 'خاتم الذهب', description: 'يرمز للسلطة أو المنصب أو الزواج، وفقدانه ينذر بضياع فرصة أو حدوث خلاف.' },
      { title: 'سبيكة الذهب', description: 'تدل على تكليف بمسؤولية شاقة أو غرامة مالية أو هموم مؤقتة.' },
      { title: 'العثور على ذهب ضائع', description: 'بشارة باسترجاع حق مسلوب أو عودة الأمل في أمر كاد الرائي ييأس منه.' },
      { title: 'سرقة الذهب', description: 'يدل على وقوع ضرر من صديق غير وفي أو خوف الرائي على مكتسباته.' }
    ],
    faqs: [
      { question: 'هل الذهب في المنام يعني دائماً خسارة للرجل؟', answer: 'ليس دائماً؛ فإذا رأى الرجل أنه يحصل على دنانير ذهبية قديمة فهو نيل ولاية أو جاه، والكراهة في الغالب ترتبط بلبس الذهب والتزين به للرجال.' },
      { question: 'ما تفسير إعطاء الميت ذهباً للحي؟', answer: 'عطاء الميت ذهباً من أعظم الرؤى المحمودة، وتدل على علو الشأن والنجاة من المهالك وقدوم رزق وفير غير متوقع.' }
    ],
    relatedSlugs: ['snake', 'marriage', 'money', 'teeth-falling', 'pregnancy'],
    datePublished: '2025-01-12',
    dateModified: '2026-08-30'
  },
  {
    slug: 'death',
    arabicSlug: 'تفسير-حلم-الموت',
    name: 'الموت وخروج الروح',
    letter: 'م',
    category: 'أحداث ومناسبات',
    categorySlug: 'events-death-marriage',
    title: 'تفسير حلم الموت في المنام لابن سيرين والنابلسي وموت شخص حي بالتفصيل',
    metaDescription: 'تفسير شامل لرؤية الموت في المنام لابن سيرين والنابلسي. موت الرائي نفسه، موت شخص عزيز، عودة الميت للحياة، والبكاء على الميت للعزباء والمتزوجة والحامل والرجل.',
    keywords: ['تفسير حلم الموت', 'موت شخص حي في المنام', 'رؤية الميت في المنام', 'تفسير ابن سيرين للموت', 'الموت والبكاء في الحلم'],
    summary: 'الموت في المنام في الأغلب الأعم ليس موتاً حقيقياً، بل يرمز إلى التوبة وطول العمر، أو قضاء الديون والشفاء للمريض، أو الانتقال من حال إلى حال أفضل، وقد يرمز أيضاً إلى موت الضمير أو الغفلة لمن كان على معصية.',
    ibnSirinInterpretation: 'أكد ابن سيرين أن الموت في الرؤيا يدل على توبة من ذنب عظيم لمن رأى نفسه مات ثم عاش، لقوله تعالى: "أَوَمَن كَانَ مَيْتًا فَأَحْيَيْنَاهُ". ومن رأى أنه مات من غير مرض ولا هيئة الموتى طال عمره. والموت مع الصراخ والعويل واللطم يدل على فساد في الدين ونقص في المال أو مصيبة دنيوية.',
    nabulsiInterpretation: 'ذكر النابلسي أن الموت يدل على سفر طويل أو زواج للأعزب، لأن الميت يحتاج إلى الغسل والطيب كما يحتاجه العروس. وموت المريض شفاء، وموت السجين حرية وخلاص، وموت الفقير غنى وسعة رزق إن لم تصحبه نوائح.',
    cases: {
      single: 'يدل على انتهاء مرحلة العزوبية والانتقال لبيت الزوجية، أو التخلص من هم كبير كان يؤرقها.',
      married: 'موت الزوج دون جنازة يدل على سفره أو بداية عمل جديد، وموت أحد الأبناء يدل على زوال هم أو حمايته من شر.',
      pregnant: 'بشارة بسلامة الولادة وتيسيرها وطول عمر المولود والتمتع بالصحة والعافية.',
      divorced: 'يرمز الموت للمطلقة إلى قطع صلتها بالماضي نهائياً وبداية حياة جديدة مشرقة والتخلص من المشاكل السابقة.',
      man: 'يدل على سداد الديون والنجاة من ورطة وتجدد الأعمال والبركة في العمر والصحة.'
    },
    keyVariations: [
      { title: 'موت شخص حي والبكاء عليه', description: 'يدل على طول عمر هذا الشخص وسلامته من مكروه وتفريج كربته.' },
      { title: 'عودة الميت للحياة', description: 'إحياء أمر ميؤوس منه، أو تجدد ذكرى طيبة للشخص وحسن حاله عند ربه.' },
      { title: 'رؤية الميت يبتسم أو يتكلم', description: 'كلام الميت حق لأنه في دار الحق، وابتسامته دليل على حسن عاقبته والرضا.' }
    ],
    faqs: [
      { question: 'هل حلمت بموت شخص يعني أنه سيموت قريباً؟', answer: 'لا إطلاقاً؛ رؤيا الموت في الغالب تعكس عمراً مديداً وتخلصاً من الأزمات للشخص الحي، ولا تعني الوفاة الحقيقية.' },
      { question: 'ما دلالة تغسيل الميت وتكفينه؟', answer: 'التكفين يدل على الستر وقضاء الديون والتوبة من الذنوب والمعاصي.' }
    ],
    relatedSlugs: ['snake', 'gold', 'sea', 'teeth-falling', 'marriage'],
    datePublished: '2025-01-15',
    dateModified: '2026-08-30'
  },
  {
    slug: 'teeth-falling',
    arabicSlug: 'تفسير-حلم-تساقط-الأسنان',
    name: 'تساقط الأسنان والضروس',
    letter: 'س',
    category: 'أعضاء الجسم والصحة',
    categorySlug: 'body-health',
    title: 'تفسير حلم تساقط الأسنان في المنام لابن سيرين والنابلسي مع وبدون دم أو ألم',
    metaDescription: 'تفسير دقيق لحلم تساقط الأسنان والضروس في المنام لابن سيرين والنابلسي. سقوط الأسنان العلوية والسفلية، باليد، وبدون ألم للعزباء والمتزوجة والحامل.',
    keywords: ['تفسير حلم تساقط الاسنان', 'سقوط الاسنان بدون دم', 'سقوط الضرس في المنام', 'تفسير ابن سيرين للاسنان', 'سقوط الاسنان في اليد'],
    summary: 'تمثل الأسنان في المنام أهل بيت الرائي وقرابته؛ فالأسنان العلوية رجال العائلة، والسفلية نساؤها، وسقوط السن في اليد أو الحجر يدل على طول العمر وقضاء الدين والرزق بالأولاد، بينما سقوطه على الأرض قد ينذر بالفقد أو المرض.',
    ibnSirinInterpretation: 'أوضح ابن سيرين أن الأسنان تدل على الأهل والقرابة؛ فالثنايا العليا تدل على الأب والعم أو الأخوين والابنين، والثنايا السفلى تدل على الأم والعمة أو الأختين والبنتين. وسقوط الأسنان كلها حتى لا يراها الرائي يدل على طول عمره حتى يعيش بعد أقرانه وأهل بيته. وسقوط السن في كف الرائي أو في ثوبه يدل على ولد يولد له أو مال يأتيه أو مصالحة مع قريب كان على خلاف معه.',
    nabulsiInterpretation: 'أشار النابلسي إلى أن من سقطت أسنانه وقدر على أكل الطعام فإنه يقضي دينه وتزول مشاكله، وإن لم يستطع الأكل بعد سقوطها فقد يصيبه فقر مؤقت. وسقوط الضرس المسوس أو التالف يدل على الشفاء من مرض أو قطع صلة بشخص منافق وخبيث.',
    cases: {
      single: 'سقوط السن في اليد للعزباء يرمز إلى اقتراب زواجها أو حصولها على مال ورزق، وسقوطه بدون ألم يدل على زوال الهموم.',
      married: 'سقوط السن في اليد للمتزوجة بشارة بالحمل بولد ذكر أو زيادة في رزق زوجها وأمان بيتها.',
      pregnant: 'دلالة على اقتراب موعد الولادة وتيسيرها وتفريج الآلام والمتاعب الجسدية.',
      divorced: 'يرمز إلى استرداد حقوقها المالية وقضاء ديونها ونهاية النزاعات العائلية.',
      man: 'سقوط السن في اليد يدل على أرباح تجارية، وسداد لكافة الديون المتراكمة والتمتع بالصحة والعافية.'
    },
    keyVariations: [
      { title: 'سقوط الأسنان بدون دم وبدون ألم', description: 'تيسير للأمور المعسرة وزوال الخلافات العائلية دون ترك أثر سلبي.' },
      { title: 'سقوط الأسنان مع دم وألم', description: 'يدل على نضج فكري أو تخلص من عبء بعد معاناة، أو تعثر مالي يسير يتبعه فرج.' },
      { title: 'سقوط الضرس العلوي', description: 'يرمز لعميد العائلة من الرجال أو الرزق بالأموال إن وقع في اليد.' }
    ],
    faqs: [
      { question: 'هل سقوط الأسنان يعني موت أحد الأقارب دائماً؟', answer: 'كلا؛ سقوط السن في اليد أو الحجر أو الثوب هو علامة واضحة على طول العمر والرزق وقضاء الدين، ولا يدل على الموت إلا إذا سقطت في التراب ودفنت مع مظاهر الحزن.' }
    ],
    relatedSlugs: ['death', 'snake', 'gold', 'marriage', 'money'],
    datePublished: '2025-01-18',
    dateModified: '2026-08-30'
  },
  {
    slug: 'sea',
    arabicSlug: 'تفسير-حلم-البحر',
    name: 'البحر والأمواج',
    letter: 'ب',
    category: 'طبيعة وعناصر',
    categorySlug: 'nature-elements',
    title: 'تفسير حلم البحر في المنام لابن سيرين والنابلسي (الهادئ والهائج والغرق)',
    metaDescription: 'دليل كامل لتفسير حلم البحر في المنام لابن سيرين والنابلسي. البحر الهادئ، البحر الهائج، السباحة، الغرق، وشرب ماء البحر للعزباء والمتزوجة والحامل والرجل.',
    keywords: ['تفسير حلم البحر', 'البحر الهائج في المنام', 'السباحة في البحر', 'الغرق في البحر والنجاة منه', 'تفسير ابن سيرين للبحر'],
    summary: 'البحر في المنام يرمز إلى السلطان العظيم، أو الدنيا بأهوالها وتقلباتها، أو العلم الغزير، أو التجارة والأرزاق الواسعة. وهدوء البحر أمان وسكون، وهيجانه فتن ومحن، والسباحة فيه نيل للمطالب والعلوم.',
    ibnSirinInterpretation: 'قال ابن سيرين إن البحر يدل على كل من له سلطان قوي كالملوك والحكام والعلماء وأرباب الأموال. فمن رأى أنه يشرب من ماء البحر فإنه يصيب من السلطان مالاً أو ينال علماً واسعاً بقدر ما شرب. والسباحة في البحر تدل على الدخول في أعمال جليلة ومنافسات كبيرة، فإن عبر البحر بسلام نجا من كل كرب وخطر.',
    nabulsiInterpretation: 'ذكر النابلسي أن البحر الهادئ الصافي بشارة بالاستقرار النفسي وسعة الأرزاق ونيل الأمنيات. أما الغرق في البحر ثم النجاة منه فيدل على النجاة من فتن الدنيا والتوبة إلى الله ونيل مكانة رفيعة بعد كد وتعب.',
    cases: {
      single: 'البحر الهادئ الصافي يدل على الزواج من رجل كريم وذي مكانة مرموقة، والسباحة بمهارة دلالة على تحقيق الطموحات.',
      married: 'رؤية البحر الهادئ للمتزوجة ترمز للراحة الأسرية والغنى وسعة الرزق في بيتها وزوال المشاحنات.',
      pregnant: 'البحر الصافي يبشر بولادة ميسرة وصحة ممتازة للمولود وسلامة الأم.',
      divorced: 'النجاة من أمواج البحر الهائج تعني تخطي كافة أزمات الماضي والوصول لبر الأمان والاستقرار النفسي.',
      man: 'السباحة في البحر ترمز إلى تجارة رابحة وترقية في العمل ونيل أرباح مضاعفة.'
    },
    keyVariations: [
      { title: 'البحر الهائج', description: 'يدل على التعرض لضغوطات وتقلبات في الحياة أو مواجهة مشكلات تتطلب الحكمة والصبر.' },
      { title: 'شرب ماء البحر العذب الصافي', description: 'دلالة على طول العمر والغنى وحيازة العلم والمنفعة الكبيرة.' },
      { title: 'المشي على ماء البحر', description: 'يدل على قوة الإيمان واليقين ونيل مرتبة شريفة ومحبة الناس.' }
    ],
    faqs: [
      { question: 'ما معنى الغرق في البحر في الحلم؟', answer: 'إن غرق واستقر في القاع فقد ينغمس في هموم الدنيا، ولكن إن نجا وخرج سالماً فهو توبة نصوح وخلاص من ورطة كبرى.' }
    ],
    relatedSlugs: ['rain', 'snake', 'gold', 'death', 'flying'],
    datePublished: '2025-01-20',
    dateModified: '2026-08-30'
  },
  {
    slug: 'black-cat',
    arabicSlug: 'تفسير-حلم-القطط-والقط-الأسود',
    name: 'القطط والقط الأسود',
    letter: 'ق',
    category: 'حيوانات وزواحف',
    categorySlug: 'animals-reptiles',
    title: 'تفسير حلم القطط والقط الأسود في المنام لابن سيرين والنابلسي بالتفصيل',
    metaDescription: 'تفسير حلم القطط والقط الأسود والأبيض في المنام لابن سيرين والنابلسي. هجوم القطط، عضة القطة، وإطعام القطط للعزباء والمتزوجة والحامل والمطلقة والرجل.',
    keywords: ['تفسير حلم القطط', 'القط الاسود في المنام', 'عضة القطة في الحلم', 'طرد القطط من البيت', 'تفسير ابن سيرين للقطط'],
    summary: 'القط في المنام قد يرمز إلى خادم أمين أو شخص متلصص حاسد من الأهل أو الجيران، والقط الأسود يدل على جفاء وخيانة أو حسد، بينما القط الأبيض الأليف أمان ولطف.',
    ibnSirinInterpretation: 'أوضح ابن سيرين أن السنور (القط) يرمز إلى لص من أهل البيت أو من خارجه، وقيل هو خادم حارس. وعضة القطة أو خدشها تدل على نكد أو مرض يستمر عاماً ثم يزول بإذن الله.',
    nabulsiInterpretation: 'ذكر النابلسي أن القطط تدل على المكر والخديعة من أشخاص يظهرون الود، وقتل القط أو طرده من البيت يدل على كشف المنافقين والتخلص من أذاهم.',
    cases: {
      single: 'تحذير من صديقة غير وفية أو شخص يتودد إليها بنية المكر، وإبعاد القطط يدل على نجاتها من مكيدة.',
      married: 'رؤية القطط في البيت قد تدل على عين حاسدة تنظر لنعيمها، وإطعامها يدل على فعل الخير ورد الأذى.',
      pregnant: 'القطة الأليفة تبشر بمولود هادئ، والقطة المهاجمة تنبه لأهمية التحصين بالأذكار والرقية الشرعية.',
      divorced: 'طرد القطط من البيت يرمز إلى طرد الطاقة السلبية والأشخاص السيئين وبداية مرحلة من الصفاء.',
      man: 'عضة القطة تحذر من خسارة في شراكة غير مدروسة، ومداعبة القط الصغير رزق وفرح.'
    },
    keyVariations: [
      { title: 'القط الأبيض الهادئ', description: 'صديق مخلص، أو خبر سار، أو أمان من المخاوف.' },
      { title: 'عضة القط الأسود', description: 'تنبيه للمحافظة على الأذكار والرقية الشرعية والابتعاد عن مواطن الشبهات.' }
    ],
    faqs: [
      { question: 'هل إطعام القطة في المنام خير؟', answer: 'نعم؛ إطعام القطط من الرؤى المحمودة الدالة على الإحسان وجلب الرزق والبركة ودفع البلاء.' }
    ],
    relatedSlugs: ['snake', 'death', 'teeth-falling', 'gold', 'sea'],
    datePublished: '2025-02-10',
    dateModified: '2026-08-30'
  }
];

const rawDreams = [...INITIAL_DREAMS, ...EXPANDED_DREAMS];
const dreamMap = new Map<string, DreamTopic>();
for (const dream of rawDreams) {
  dreamMap.set(dream.slug, dream);
}

export const DREAMS_DATABASE: DreamTopic[] = Array.from(dreamMap.values()).map(enrichDreamArticle);

const dictionaryDreamCache = new Map<string, DreamTopic>();

export function detectSourcesFromText(text: string): { hasIbnSirin: boolean; hasNabulsi: boolean; hasIbnShahin: boolean } {
  const t = text || '';
  return {
    hasIbnSirin: /ابن سيرين|محمد بن سيرين|ابنِ سيرين/.test(t),
    hasNabulsi: /النابلسي|عبد الغني النابلسي|النابلسيّ/.test(t),
    hasIbnShahin: /ابن شاهين|شاهين|ابنِ شاهين/.test(t),
  };
}

export function formatSourcesLabel(sources: { hasIbnSirin: boolean; hasNabulsi: boolean; hasIbnShahin: boolean }): string {
  const parts: string[] = [];
  if (sources.hasIbnSirin) parts.push('ابن سيرين');
  if (sources.hasNabulsi) parts.push('النابلسي');
  if (sources.hasIbnShahin) parts.push('ابن شاهين');
  if (parts.length === 0) parts.push('مصادر تراثية');
  return parts.join(' و');
}

// المصادر المتاحة فعلياً لصفحة الرمز، مكتشفة من مادة الصفحة الحقيقية لا من افتراضات عامة.
export function getDreamPageSources(dream: DreamTopic): { hasIbnSirin: boolean; hasNabulsi: boolean; hasIbnShahin: boolean } {
  const text = [dream.ibnSirinInterpretation, dream.nabulsiInterpretation, dream.scholarlyNuances, dream.summary].filter(Boolean).join(' ');
  return detectSourcesFromText(text);
}

export function dreamHasCases(dream: DreamTopic): boolean {
  return !!(dream.cases.single || dream.cases.married || dream.cases.pregnant || dream.cases.divorced || dream.cases.man);
}

export function normalizeArabicLetterRoot(letter: string): string {
  if (!letter) return letter;
  let l = letter.trim();
  l = l.replace(/[\u064B-\u0652\u0640]/g, '');
  const first = l.charAt(0);
  if (/[أإآاٱاء]/.test(first)) return 'أ';
  if (first === 'ى' || first === 'ئ' || first === 'ي') return 'ي';
  if (first === 'ؤ' || first === 'و') return 'و';
  if (first === 'ة') return 'هـ';
  return first;
}

export function dreamResolvesToFeaturedSlug(slug: string, arabicSlug?: string): string | null {
  const fromEditorial = arabicSlug ? (EDITORIAL_RESOLUTION[arabicSlug] || null) : null;
  if (fromEditorial) return fromEditorial;
  const entry = findDictionaryBySlug(slug);
  if (entry && entry.canonicalSlug) return entry.canonicalSlug;
  return null;
}

export function dictionaryEntryToDreamTopic(entry: DictionaryEntry): DreamTopic {
  const cached = dictionaryDreamCache.get(entry.slug);
  if (cached) return cached;

  const text = entry.text || '';
  const sources = detectSourcesFromText(text);
  const sourcesLabel = formatSourcesLabel(sources);

  const titleSources = sources.hasIbnSirin && sources.hasNabulsi
    ? 'لابن سيرين والنابلسي'
    : sources.hasIbnSirin
      ? 'لابن سيرين'
      : sources.hasNabulsi
        ? 'للنابلسي'
        : sources.hasIbnShahin
          ? 'لابن شاهين'
          : '';

  const enrichment = EDITORIAL_ENRICHMENT[entry.slug];
  const related = enrichment?.relatedSlugs?.length
    ? enrichment.relatedSlugs
    : DICTIONARY_DB
        .filter((d) => d.letter === entry.letter && d.slug !== entry.slug)
        .slice(0, 6)
        .map((d) => d.slug);

  const topic: DreamTopic = {
    slug: entry.slug,
    arabicSlug: entry.arabicSlug,
    name: entry.name,
    letter: entry.letter,
    category: entry.category,
    categorySlug: entry.categorySlug,
    title: titleSources
      ? `تفسير ${entry.name} في المنام ${titleSources} | قاموس الرؤى`
      : `تفسير ${entry.name} في المنام | قاموس الرؤى التراثي`,
    metaDescription: enrichment?.metaDescription && enrichment.metaDescription.length > 0
      ? enrichment.metaDescription
      : `تفسير رؤية ${entry.name} في المنام مستنداً إلى ${sourcesLabel}. ${text.slice(0, 130)}`,
    keywords: [`تفسير حلم ${entry.name}`, `رؤية ${entry.name} في المنام`, `${entry.name} في المنام`],
    summary: text,
    quickAnswer: enrichment?.quickAnswer,
    editorialMeaning: enrichment?.editorialMeaning,
    ibnSirinInterpretation: '',
    nabulsiInterpretation: '',
    cases: { single: '', married: '', pregnant: '', divorced: '', man: '' },
    keyVariations: enrichment?.keyVariations ?? [],
    faqs: enrichment?.faqs ?? [],
    relatedSlugs: related,
    datePublished: '2025-01-01',
    dateModified: '2026-09-20',
    isDictionary: true,
    sourceResolved: enrichment?.quickAnswer ? 'editorial' : 'dictionary',
  };
  dictionaryDreamCache.set(entry.slug, topic);
  return topic;
}

function resolveDictionarySlugToDreamTopic(slug: string): DreamTopic | undefined {
  const entry = findDictionaryBySlug(slug);
  if (!entry) return undefined;
  if (entry.canonicalSlug) {
    return findDreamBySlug(entry.canonicalSlug);
  }
  return dictionaryEntryToDreamTopic(entry);
}

export function findDreamBySlug(slug: string): DreamTopic | undefined {
  const decoded = decodeURIComponent(slug).trim().toLowerCase();
  const direct = DREAMS_DATABASE.find(d => 
    d.slug.toLowerCase() === decoded || 
    d.arabicSlug === decoded ||
    d.slug.toLowerCase() === slug.toLowerCase()
  );
  if (direct) return direct;
  return resolveDictionarySlugToDreamTopic(slug);
}

const featuredSlugsGlobal = new Set(DREAMS_DATABASE.map(d => d.slug));

export function getCanonicalDictionaryEntries(): DictionaryEntry[] {
  return DICTIONARY_DB.filter(entry => {
    if (entry.canonicalSlug) return false;
    const editorialTarget = EDITORIAL_RESOLUTION[entry.arabicSlug];
    if (editorialTarget && editorialTarget !== entry.slug) return false;
    if (editorialTarget && featuredSlugsGlobal.has(editorialTarget)) return false;
    if (featuredSlugsGlobal.has(entry.slug)) return false;
    return true;
  });
}

const enrichedDictSlugs = new Set<string>(Object.keys(EDITORIAL_ENRICHMENT));

export interface GroupedSymbols {
  enhanced: DreamTopic[];
  plain: DictionaryEntry[];
}

// تقسيم غير متكرر: الرموز المحسّنة (مميزة أو بصياغة تحريرية) ثم بقية رموز القاموس.
export function getLetterGrouped(letter: string): GroupedSymbols {
  const normalized = normalizeArabicLetterRoot(letter);
  const canonical = getCanonicalDictionaryEntries();
  const enrichedDict = canonical
    .filter(e => normalizeArabicLetterRoot(e.letter) === normalized && enrichedDictSlugs.has(e.slug))
    .map(dictionaryEntryToDreamTopic);
  const plain = canonical.filter(e => normalizeArabicLetterRoot(e.letter) === normalized && !enrichedDictSlugs.has(e.slug));
  return {
    enhanced: [...DREAMS_DATABASE.filter(d => normalizeArabicLetterRoot(d.letter) === normalized), ...enrichedDict],
    plain
  };
}

export function getCategoryGrouped(categorySlug: string): GroupedSymbols {
  const canonical = getCanonicalDictionaryEntries();
  const enrichedDict = canonical
    .filter(e => e.categorySlug === categorySlug && enrichedDictSlugs.has(e.slug))
    .map(dictionaryEntryToDreamTopic);
  const plain = canonical.filter(e => e.categorySlug === categorySlug && !enrichedDictSlugs.has(e.slug));
  return {
    enhanced: [...DREAMS_DATABASE.filter(d => d.categorySlug === categorySlug), ...enrichedDict],
    plain
  };
}

export function getDreamsByLetter(letter: string): DreamTopic[] {
  const normalized = normalizeArabicLetterRoot(letter);
  const fromFeatured = DREAMS_DATABASE.filter(d => normalizeArabicLetterRoot(d.letter) === normalized);
  const dictEntries = getCanonicalDictionaryEntries()
    .filter(e => normalizeArabicLetterRoot(e.letter) === normalized)
    .map(dictionaryEntryToDreamTopic);
  return [...fromFeatured, ...dictEntries];
}

export function getDreamsByCategory(categorySlug: string): DreamTopic[] {
  const fromFeatured = DREAMS_DATABASE.filter(d => d.categorySlug === categorySlug);
  const dictEntries = getCanonicalDictionaryEntries()
    .filter(e => e.categorySlug === categorySlug)
    .map(dictionaryEntryToDreamTopic);
  return [...fromFeatured, ...dictEntries];
}
